import React, { useState, useEffect } from "react"; // Added useEffect
import { useGeld } from "../context/GeldContext";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";

const GeldStore = ({ onClose }) => {
  const { geldBalance, spendGeld } = useGeld();
  const { user, updateUserData, supabase } = useAuth(); // Added supabase
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("avatars");
  const [confirmItem, setConfirmItem] = useState(null);
  const [ownedItems, setOwnedItems] = useState(new Set()); // State to track owned items
  const [loadingInventory, setLoadingInventory] = useState(true); // Loading state for inventory check

  // Fetch owned items when the component mounts or user changes
  useEffect(() => {
    const fetchOwnedItems = async () => {
      if (!user || !supabase) return;
      setLoadingInventory(true);
      try {
        const { data, error } = await supabase
          .from("user_inventory")
          .select("item_id")
          .eq("user_id", user.id);

        if (error) throw error;

        setOwnedItems(new Set(data.map((item) => item.item_id)));
      } catch (error) {
        console.error("Error fetching owned items:", error);
        // Handle error appropriately, maybe show a message
      } finally {
        setLoadingInventory(false);
      }
    };

    fetchOwnedItems();
  }, [user, supabase]);

  // Store items categorized - Removed powerups
  const storeItems = {
    avatars: [
      {
        id: "avatar_knight",
        name: "Knight Avatar",
        description: "Unlock the special knight avatar for your profile",
        price: 50,
        avatarProps: {
          avatar_hair_type: "short",
          avatar_hair: "#A9A9A9",
          avatar_shirt: "#808080",
          avatar_eyes: "#0000FF",
          avatar_skin: "#FFE0B2",
        },
        fallbackColor: "#4CAF50",
      },
      {
        id: "avatar_wizard",
        name: "Wizard Avatar",
        description: "Unlock the magical wizard avatar for your profile",
        price: 75,
        avatarProps: {
          avatar_hair_type: "long",
          avatar_hair: "#FFFFFF",
          avatar_shirt: "#9C27B0",
          avatar_eyes: "#FFD700",
          avatar_skin: "#FFCCBC",
        },
        fallbackColor: "#2196F3",
      },
    ],
    backgrounds: [
      {
        id: "bg_castle",
        name: "Castle Background",
        description: "A majestic castle background for your profile",
        price: 40,
        previewStyle: { backgroundColor: "#795548" },
        fallbackColor: "#795548",
      },
      {
        id: "bg_forest",
        name: "Enchanted Forest",
        description: "A magical forest background for your profile",
        price: 40,
        previewStyle: { backgroundColor: "#388E3C" },
        fallbackColor: "#388E3C",
      },
    ],
    borders: [
      {
        id: "border_castle",
        name: "Castle Borders",
        description: "Medieval castle-themed borders for your math workspace",
        price: 60,
        previewStyle: {
          backgroundImage: "url('/borders/castle/border-left.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#8B4513",
        },
        fallbackColor: "#8B4513",
      },
      {
        id: "border_forest",
        name: "Forest Borders",
        description: "Enchanted forest-themed borders for your math workspace",
        price: 60,
        previewStyle: {
          backgroundImage: "url('/borders/forest/border-left.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#2E7D32",
        },
        fallbackColor: "#2E7D32",
      },
      {
        id: "border_runic",
        name: "Runic Borders",
        description: "Mystical runic-themed borders for your math workspace",
        price: 60,
        previewStyle: {
          backgroundImage: "url('/borders/runic/border-left.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#5D4037",
        },
        fallbackColor: "#5D4037",
      },
      {
        id: "border_bookend",
        name: "Bookend Borders",
        description: "Classic bookend-themed borders for your math workspace",
        price: 60,
        previewStyle: {
          backgroundImage: "url('/borders/bookend/border-left.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#795548",
        },
        fallbackColor: "#795548",
      },
    ],
  };

  // Categories for the store - Added borders
  const categories = [
    { id: "avatars", name: "Avatars" },
    { id: "backgrounds", name: "Backgrounds" },
    { id: "borders", name: "Borders" },
  ];

  // Handle purchase attempt
  const handlePurchase = async (item) => {
    if (geldBalance < item.price) {
      setPurchaseStatus({
        success: false,
        message: "Not enough geld for this purchase",
      });
      setTimeout(() => setPurchaseStatus(null), 3000);
      return;
    }

    setConfirmItem(item);
  };

  // Confirm purchase
  const confirmPurchase = async (item) => {
    // Check if already owned before attempting to spend geld
    if (ownedItems.has(item.id)) {
      setPurchaseStatus({
        success: false,
        message: "You already own this item!",
      });
      setConfirmItem(null);
      setTimeout(() => setPurchaseStatus(null), 3000);
      return;
    }

    const result = await spendGeld(item.price, `store_purchase_${item.id}`);

    if (result.success) {
      // Apply the purchased item effect
      try {
        let itemApplied = false; // Flag to track if item effect was applied
        if (selectedCategory === "avatars" && item.avatarProps) {
          // Update user's avatar in the database
          const avatarUpdates = {
            avatar_hair_type: item.avatarProps.avatar_hair_type,
            avatar_hair: item.avatarProps.avatar_hair,
            avatar_shirt: item.avatarProps.avatar_shirt,
            avatar_eyes: item.avatarProps.avatar_eyes,
            avatar_skin: item.avatarProps.avatar_skin,
          };
          console.log("Attempting to update user avatar with:", avatarUpdates);
          await updateUserData(avatarUpdates);
          console.log(`Avatar updated successfully for user: ${user.id}`);
          itemApplied = true;
        } else if (selectedCategory === "backgrounds" && item.id) {
          // Placeholder for updating background - requires schema change
          // await updateUserData({ profile_background_id: item.id });
          console.log(
            `Background purchase logic for ${item.id} needs implementation (DB update)`
          );
          itemApplied = true; // Mark as applied even if only logged for now
        } else if (selectedCategory === "borders" && item.id) {
          // Extract theme name from border ID (e.g., "border_castle" -> "castle")
          const themeName = item.id.replace("border_", "");
          await updateUserData({ selected_border_theme: themeName });
          console.log(`Border theme updated to: ${themeName}`);
          itemApplied = true;
        }

        // Add item to inventory table AFTER successful purchase and application
        const { error: inventoryError } = await supabase
          .from("user_inventory")
          .insert({
            user_id: user.id,
            item_id: item.id,
            item_type: selectedCategory,
          });

        if (inventoryError) {
          console.error("Failed to add item to inventory:", inventoryError);
          setPurchaseStatus({
            success: false,
            message:
              "Purchase complete, but failed to save to inventory. Contact support.",
          });
        } else {
          console.log("Item added to inventory:", item.id);
          setOwnedItems((prev) => new Set(prev).add(item.id));
          setPurchaseStatus({
            success: true,
            message: `Successfully purchased ${
              itemApplied ? "and equipped " : ""
            }${item.name}!`,
          });
        }

        console.log(`Item purchased: ${item.id}`);
      } catch (updateError) {
        console.error(
          "Failed to apply purchased item or add to inventory:",
          updateError
        );
        setPurchaseStatus({
          success: false,
          message:
            "Purchase failed while applying item. Geld may have been deducted. Contact support.",
        });
      }

      setConfirmItem(null);
    } else {
      setPurchaseStatus({
        success: false,
        message: result.message || "Failed to complete purchase",
      });
      setConfirmItem(null);
    }

    setTimeout(() => setPurchaseStatus(null), 3000);
  };

  return (
    <div className="geld-store-overlay" onClick={() => onClose && onClose()}>
      <style jsx="true">{`
        .geld-store-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .geld-store {
          background-color: white;
          border-radius: 16px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          padding: 20px;
          position: relative;
        }

        .store-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 2px solid #f3e1b2;
          padding-bottom: 10px;
          padding-right: 20px; /* Add space so geld balance doesn't get too close to the close button */
        }

        .store-title {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          font-family: "Inter", sans-serif;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .geld-balance {
          display: flex;
          align-items: center;
          background-color: #f3e1b2;
          border-radius: 12px;
          padding: 6px 12px;
          font-weight: 600;
          gap: 5px;
        }

        .category-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          overflow-x: auto;
          padding-bottom: 5px;
        }

        .category-tab {
          background-color: #f5f5f5;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          white-space: nowrap;
          font-weight: 500;
          transition: all 0.2s;
          font-family: "Inter", sans-serif;
        }

        .category-tab.active {
          background-color: #f3e1b2;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .store-items {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 20px;
        }

        .store-item {
          border: 1px solid #eee;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          flex-direction: column;
          transition: all 0.2s;
          cursor: pointer;
        }

        .store-item:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .item-image {
          width: 100%;
          aspect-ratio: 1;
          border-radius: 8px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          overflow: hidden; /* Ensure avatar fits */
        }

        .avatar-preview-container {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 100%;
          background-color: #f0f0f0; /* Light background for avatar */
        }

        .background-preview {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          text-align: center;
          padding: 5px;
        }

        .item-details {
          flex: 1;
        }

        .item-name {
          font-weight: 600;
          margin-bottom: 5px;
          font-family: "Inter", sans-serif;
        }

        .item-description {
          font-size: 12px;
          color: #666;
          margin-bottom: 10px;
          font-family: "Inter", sans-serif;
        }

        .item-price {
          display: flex;
          align-items: center;
          gap: 5px;
          font-weight: 600;
          margin-top: auto;
          font-family: "Inter", sans-serif;
        }

        .purchase-button {
          background-color: #4caf50;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          margin-top: 10px;
          font-weight: 500;
          transition: background-color 0.2s;
          width: 100%;
          font-family: "Inter", sans-serif;
        }

        .purchase-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }

        .purchase-button:hover:not(:disabled) {
          background-color: #388e3c;
        }

        .close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }

        .status-message {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 10px 20px;
          border-radius: 8px;
          color: white;
          font-weight: 500;
          z-index: 2000;
          animation: fadeIn 0.3s ease-out;
        }

        .status-message.success {
          background-color: #4caf50;
        }

        .status-message.error {
          background-color: #f44336;
        }

        .confirmation-modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background-color: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
          z-index: 1100;
          width: 90%;
          max-width: 400px;
        }

        .confirmation-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 10px;
          font-family: "Inter", sans-serif;
        }

        .confirmation-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }

        .confirm-button {
          background-color: #4caf50;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
        }

        .cancel-button {
          background-color: #9e9e9e;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      <div className="geld-store" onClick={(e) => e.stopPropagation()}>
        <div className="store-header">
          <h2 className="store-title">
            <img
              src="/geld.png"
              alt="geld"
              style={{
                width: "24px",
                height: "24px",
              }}
            />
            Geld Store
          </h2>
          <div className="geld-balance">
            <img
              src="/geld.png"
              alt="geld"
              style={{
                width: "18px",
                height: "18px",
              }}
            />
            <span>{geldBalance}</span>
          </div>
        </div>

        <div className="category-tabs">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`category-tab ${
                selectedCategory === category.id ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </div>
          ))}
        </div>

        <div className="store-items">
          {loadingInventory ? (
            <p>Loading store...</p> // Show loading indicator while checking inventory
          ) : (
            storeItems[selectedCategory]?.map((item) => {
              const isOwned = ownedItems.has(item.id);
              return (
                <div key={item.id} className="store-item">
                  <div
                    className="item-image"
                    style={{
                      backgroundColor: item.fallbackColor,
                    }}
                  >
                    {/* Conditional rendering for previews */}
                    {selectedCategory === "avatars" && item.avatarProps ? (
                      <div className="avatar-preview-container">
                        <UserAvatar user={item.avatarProps} size={100} />
                      </div>
                    ) : selectedCategory === "backgrounds" &&
                      item.previewStyle ? (
                      <div
                        className="background-preview"
                        style={item.previewStyle}
                      >
                        {/* Optionally add text or icon here */}
                      </div>
                    ) : selectedCategory === "borders" ? (
                      <div
                        className="border-preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundImage: `url('/borders/${item.id.replace(
                            "border_",
                            ""
                          )}/border-left.png')`,
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          backgroundColor: "#FFFFFF",
                        }}
                      />
                    ) : (
                      // Fallback if no specific preview is available
                      item.name.charAt(0)
                    )}
                  </div>
                  <div className="item-details">
                    <div className="item-name">{item.name}</div>
                    <div className="item-description">{item.description}</div>
                    <div className="item-price">
                      <img
                        src="/geld.png"
                        alt="geld"
                        style={{
                          width: "16px",
                          height: "16px",
                        }}
                      />
                      {item.price}
                    </div>
                    <button
                      className="purchase-button"
                      disabled={
                        isOwned || geldBalance < item.price || loadingInventory
                      }
                      onClick={() => handlePurchase(item)}
                    >
                      {isOwned
                        ? "Owned"
                        : geldBalance < item.price
                        ? "Not enough geld"
                        : "Purchase"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <button className="close-button" onClick={() => onClose && onClose()}>
          &times;
        </button>

        {purchaseStatus && (
          <div
            className={`status-message ${
              purchaseStatus.success ? "success" : "error"
            }`}
          >
            {purchaseStatus.message}
          </div>
        )}

        {confirmItem && (
          <div
            className="confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirmation-title">
              Confirm purchase: {confirmItem.name}
            </div>
            <p>
              Are you sure you want to spend {confirmItem.price} geld on this
              item?
            </p>
            <div className="confirmation-buttons">
              <button
                className="cancel-button"
                onClick={() => setConfirmItem(null)}
              >
                Cancel
              </button>
              <button
                className="confirm-button"
                onClick={() => confirmPurchase(confirmItem)}
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeldStore;
